import { xAccessor, yAccessor } from './dot-accessors.js';
import { DotCanvas } from '../../../core/components/canvas/components/DotCanvas.js';
import { useGridPlotSize } from '../../../core/components/chart-grid/hooks/useGridPlotSize.js';
import { DEFAULT_INACTIVE_FILL_OPACITY } from '../../../core/constants.js';
import { safeGet } from '../../../core/utils/safe-get.js';
import type { AxisIdToScales } from '../../context/XYChartScales.context.js';
import { useDotSeriesData } from '../../hooks/useDotSeriesData.js';
import type { XYChartState } from '../../types/state.js';
import { getCurrentScale } from '../../utils/get-scale.js';

type HighlightedSeriesIds = XYChartState['highlightedSeriesIds'];

export function getSeriesOpacity(
  highlightedSeriesIds: HighlightedSeriesIds,
  seriesId: string,
) {
  return highlightedSeriesIds?.length &&
    !highlightedSeriesIds.includes(seriesId)
    ? DEFAULT_INACTIVE_FILL_OPACITY
    : 1;
}

export function DotDisplay(props: {
  axisIdToScales: AxisIdToScales;
  highlightedSeriesIds: HighlightedSeriesIds;
}) {
  const { axisIdToScales, highlightedSeriesIds } = props;
  const { height, width } = useGridPlotSize();
  const { seriesWithDatapoints, seriesToMetadata } = useDotSeriesData();

  return seriesWithDatapoints.map((series) => {
    const xScale = getCurrentScale(axisIdToScales, series.xAxisId);
    const yScale = getCurrentScale(axisIdToScales, series.yAxisId);
    const { color, shape } = safeGet(seriesToMetadata, series);

    return (
      <DotCanvas
        key={series.id}
        data={series.data}
        canvasHeight={height}
        canvasWidth={width}
        color={() => color.Default}
        opacity={getSeriesOpacity(highlightedSeriesIds, series.id)}
        x={xAccessor(xScale)}
        y={yAccessor(yScale)}
        shape={shape}
      />
    );
  });
}

DotDisplay['displayName'] = 'DotDisplay';
